<template>
    <div class="info-bar-wrapper">
        <div class="info-bar" :class="{ 'two-statuses': hasTwoStatuses }">
            <div class="left">
                <div class="info-text">
                    <span>
                        Operationsanmälan skapad <span class="nowrap">{{ createdAt }}</span>
                        {{ createdBy ? `av ${createdBy}, ${createdByRoll}` : "" }}
                    </span>
                    <span v-if="showUpdated">
                        <PipeDivider />
                        Senast redigerad <span class="nowrap">{{ updatedAt }}</span>
                        {{ updatedBy ? `av ${updatedBy}, ${updatedByRoll}` : "" }}
                    </span>
                    <span v-if="operationsNamn.length">
                        <PipeDivider />
                        {{ operationsNamn }}
                    </span>
                </div>
            </div>
            <div class="right">
                <div class="statuses">
                    <Status
                        v-if="showPreanestesibedomningStatus"
                        variant="small"
                        :text="anmalan.preanestesibedomning_godkannande || ''"
                        :status="anmalan.preanestesibedomning_godkannande"
                        :icons="preanestesiStatusIcons"
                    />

                    <Status
                        v-if="showPreliminarbokningStatus"
                        variant="small"
                        text="Preliminärbokad"
                        :status="true"
                        :icons="{ true: PreliminarbokningIcon }"
                    />
                    <Status
                        variant="small"
                        class="justify-end"
                        :text="anmalan?.status || ''"
                        :status="status"
                        :icons="anmalanStatusIcons"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import PipeDivider from "@/components/generic/PipeDivider.vue";
    import Status from "@/tabs/Operation/Status.vue";
    import { anmalanStatusIcons, preanestesiStatusIcons } from "@/tabs/Operation/utilsTs";
    import { computed } from "vue";
    import PreliminarbokningIcon from "@/assets/preliminarbokning.svg";

    interface AnmalanPreliminaryData {
        createdBy: string;
        createdAt: string;
        createdByRoll: string;
        anmalanStatus?: string;
    }
    const props = withDefaults(
        defineProps<{
            anmalan: any;
            creatingAnmalan?: boolean;
            creatingPreliminaryData?: AnmalanPreliminaryData;
            showUpdated?: boolean;
            showPreanestesibedömningStatus?: boolean;
        }>(),
        {
            creatingAnmalan: false,
            showUpdated: true,
            showPreanestesibedömningStatus: false,
        }
    );

    const createdAt = computed(() => {
        if (props.creatingAnmalan) {
            return props.creatingPreliminaryData?.createdAt || "";
        }
        return props?.anmalan?.created_at ? new Date(props?.anmalan?.created_at).toLocaleDateString("sv-SE") : "";
    });

    const createdBy = computed(() => {
        if (props.creatingAnmalan) {
            return props.creatingPreliminaryData?.createdBy || "";
        }
        return props?.anmalan?.created_by_display?.user_display?.name || "";
    });
    const createdByRoll = computed(() => {
        if (props.creatingAnmalan) {
            return props.creatingPreliminaryData?.createdByRoll || "";
        }
        return props?.anmalan?.created_by_display?.roll_display?.displayName || "";
    });
    const updatedBy = computed(() => {
        if (props.creatingAnmalan) {
            return "";
        }
        return props?.anmalan?.updated_by_display?.user_display?.name || "";
    });
    const status = computed(() => {
        return props.creatingAnmalan ? props.creatingPreliminaryData?.anmalanStatus : props?.anmalan?.status;
    });

    const updatedAt = computed(() => {
        if (props.creatingAnmalan) {
            return false;
        }
        return props?.anmalan?.updated_at ? new Date(props?.anmalan?.updated_at).toLocaleDateString("sv-SE") : "";
    });
    const updatedByRoll = computed(() => {
        if (props.creatingAnmalan) {
            return "";
        }
        return props?.anmalan?.updated_by_display?.roll_display?.displayName || "";
    });
    const operationsNamn = computed(() => {
        if (props.creatingAnmalan) {
            return "";
        }
        if (props?.anmalan?.typoperation_display?.namn) {
            return props.anmalan.typoperation_display?.namn;
        } else if (props?.anmalan?.huvudoperationskod_display) {
            return `${props.anmalan.huvudoperationskod_display?.code || ""} ${
                props.anmalan.huvudoperationskod_display?.displayName || ""
            }`.trim();
        }
        return "";
    });

    const showPreanestesibedomningStatus = computed(() =>
        Boolean(props.showPreanestesibedömningStatus && props.anmalan?.preanestesibedomning_godkannande)
    );

    const showPreliminarbokningStatus = computed(() =>
        Boolean(!props.showPreanestesibedömningStatus && props.anmalan?.preliminarbokning)
    );

    const hasTwoStatuses = computed(() => showPreanestesibedomningStatus.value || showPreliminarbokningStatus.value);
</script>

<style lang="scss" scoped>
    @import "@/style/variables";
    @import "@/style/container";
    @import "@/style/components";
    @import "@/style/utilitary";

    .info-bar-wrapper {
        container-type: inline-size;
        container-name: infobarwrapper;
    }
    .info-bar {
        font-size: 13px;
        border-radius: $border-radius-max;
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 10px;
        background-color: $color-tablerow;
        gap: 1rem;
        margin-bottom: 1rem;
        max-width: 100%;
        color: #55676d;

        .left {
            display: flex;
            flex-wrap: wrap;
        }

        .right {
            display: flex;
            align-items: end;
            justify-content: end;
            font-weight: 500;
            color: $tabs-text-color;

            .statuses {
                display: flex;
                flex-basis: 100%;
                justify-content: end;
            }
        }

        &.two-statuses {
            .right .statuses {
                flex-direction: column;
                align-items: end;
            }
        }

        @media print {
            margin: 0;
            padding: 0;
        }

        @media screen and (max-width: 1332px) {
            .right {
                flex-direction: column;
                justify-content: center;

                .statuses {
                    flex-direction: column;
                    flex-wrap: wrap;
                    justify-content: end;
                }
            }
        }
    }

    @container infobarwrapper (max-width: 550px) {
        .info-bar {
            align-items: start;
            flex-direction: column;
        }
        .statuses,
        .two-statuses .right .statuses {
            align-items: start !important;
        }
    }
</style>
